import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatures } from '../../contexts/FeaturesContext';
import Logo from '../Logo';

function SidebarLink({ to, label, end }: { to: string; label: string; end?: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-2.5 text-sm font-semibold px-3.5 py-2.5 rounded-lg transition-colors ${
          isActive ? 'bg-accent-500 text-primary-900' : 'text-[#c3cede] hover:bg-white/5'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span className={`w-2 h-2 rounded-sm flex-shrink-0 ${isActive ? 'bg-primary-900' : 'bg-[#5b6889]'}`} />
          {label}
        </>
      )}
    </NavLink>
  );
}

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { features } = useFeatures();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex min-h-[calc(100vh-7rem)] relative">
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden fixed inset-0 bg-[#0c1c36]/50 z-40"
        />
      )}

      {/* Sidebar */}
      <div
        className={`w-60 bg-[#0c1c36] px-4 py-6 flex-col gap-1 flex-shrink-0 fixed lg:sticky top-0 lg:top-28 bottom-0 lg:h-[calc(100vh-7rem)] z-50 lg:z-0 transition-transform lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0 flex' : '-translate-x-full flex lg:flex'
        }`}
      >
        <div className="flex items-center gap-2.5 px-1 pb-5 lg:hidden">
          <Logo size={30} className="brightness-0 invert" />
          <span className="font-heading font-extrabold text-xs text-white leading-tight">
            GRENZLAND<br /><span className="text-accent-400 tracking-wide">PADEL LIGA</span>
          </span>
        </div>

        <div className="text-[#5b6889] text-[11px] font-bold tracking-wider px-3.5 pb-1.5">MENÜ</div>
        <SidebarLink to="/admin" end label={t('admin.dashTitle')} />
        {features.liga && <SidebarLink to="/leagues" label={t('nav.leagues')} />}
        {features.saison && <SidebarLink to="/seasons" label={t('nav.seasons')} />}
        {features.saison && <SidebarLink to="/ranking" label={t('nav.ranking')} />}
        <SidebarLink to="/courts" label={t('nav.courts')} />

        <div className="text-[#5b6889] text-[11px] font-bold tracking-wider px-3.5 pt-4 pb-1.5">VERWALTUNG</div>
        <SidebarLink to="/admin/users" label={t('admin.userManagement')} />
        <SidebarLink to="/admin/audit-log" label={t('admin.auditLog')} />

        <div className="mt-auto flex items-center gap-2.5 px-2.5 pt-3.5 border-t border-white/10">
          <div className="w-8 h-8 rounded-full bg-[#33456b] flex-shrink-0 flex items-center justify-center text-white text-xs font-semibold">
            {user?.first_name?.[0]}
          </div>
          <span className="text-sm text-white font-medium truncate">{user?.first_name}</span>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 bg-[#f5f7fb] min-w-0">
        <div className="flex items-center gap-3 px-4 sm:px-6 py-3 bg-white border-b border-gray-100">
          <button
            onClick={() => setSidebarOpen((v) => !v)}
            className="lg:hidden flex flex-col gap-1 p-1.5"
            aria-label={t('nav.menu')}
          >
            <span className="w-5 h-0.5 bg-primary-800" />
            <span className="w-5 h-0.5 bg-primary-800" />
            <span className="w-5 h-0.5 bg-primary-800" />
          </button>
          <button onClick={() => navigate('/')} className="text-sm text-gray-500 hover:text-gray-700">
            ← {t('footer.backToHome')}
          </button>
        </div>
        <div className="py-2">{children}</div>
      </div>
    </div>
  );
}
